/** Build an optimistic, not-yet-sent client message. */
export function makeOptimistic(input) {
    return {
        _id: null,
        tempId: input.tempId,
        // placeholder; the store backfills threadId once the server message is reconciled.
        threadId: '',
        authorUserId: input.authorUserId,
        authorDisplayName: input.authorDisplayName,
        body: input.body,
        mentions: input.mentions,
        reactions: [],
        visibility: 'internal',
        editedAt: null,
        deletedAt: null,
        createdAt: input.createdAt,
        status: 'sending',
    };
}
/** Wrap a server `Message` as a settled (`sent`) client message. */
export function fromServer(msg, tempId) {
    return { ...msg, tempId, status: 'sent' };
}
/**
 * Insert or reconcile a server message into the list, idempotent by `_id`.
 *
 * Collapses to a single entry for this message: drops any existing entry that is
 * either our optimistic copy (matched by `tempId`) OR already carries the server
 * `_id`, then re-adds the reconciled server message. This holds regardless of
 * event ordering — the sender's own `message:new` echo (which carries no tempId)
 * can race ahead of the POST response, and a mentioned viewer can receive the
 * same event twice (thread room + user room); neither can produce two entries
 * sharing one `_id`.
 *
 * A pure echo of an already-present message (no optimistic match) is a no-op,
 * returning the same array reference to keep React renders stable.
 */
export function upsertMessage(list, server, tempId) {
    const normalized = list.map((m) => 'status' in m ? m : fromServer(m));
    const hadOptimistic = !!tempId && normalized.some((m) => m.tempId === tempId && m._id === null);
    const hadServer = normalized.some((m) => m._id !== null && m._id === server._id);
    // Already present and nothing to reconcile -> no-op (stable reference).
    if (!hadOptimistic && hadServer)
        return normalized;
    const rest = normalized.filter((m) => !(!!tempId && m.tempId === tempId && m._id === null) && m._id !== server._id);
    return sortByCreatedAtThenId([...rest, fromServer(server, tempId)]);
}
/** Mark an optimistic message failed (send rejected); keeps it for retry. */
export function markFailed(list, tempId) {
    return list.map((m) => (m.tempId === tempId && m._id === null ? { ...m, status: 'failed' } : m));
}
/** Remove an optimistic message (e.g. on discard). */
export function removeOptimistic(list, tempId) {
    return list.filter((m) => !(m.tempId === tempId && m._id === null));
}
export function applyEdited(list, ev) {
    return list.map((m) => m._id === ev.messageId ? { ...m, body: ev.body, mentions: ev.mentions, editedAt: ev.editedAt } : m);
}
export function applyDeleted(list, ev) {
    return list.map((m) => 
    // tombstone: clear body + mentions; reactions are intentionally preserved.
    m._id === ev.messageId ? { ...m, body: '', mentions: [], deletedAt: ev.deletedAt } : m);
}
export function applyReactions(list, ev) {
    return list.map((m) => (m._id === ev.messageId ? { ...m, reactions: ev.reactions } : m));
}
/** Stable sort: oldest -> newest by (createdAt, _id). Optimistic (_id null) sort last within a tie. */
export function sortByCreatedAtThenId(list) {
    return list.slice().sort((a, b) => {
        if (a.createdAt < b.createdAt)
            return -1;
        if (a.createdAt > b.createdAt)
            return 1;
        // createdAt tie: optimistic messages (null _id) sort last; otherwise by _id asc.
        if (a._id === null && b._id === null)
            return 0;
        if (a._id === null)
            return 1;
        if (b._id === null)
            return -1;
        if (a._id < b._id)
            return -1;
        if (a._id > b._id)
            return 1;
        return 0;
    });
}
/** Escape regex metacharacters so a string can be used as a literal pattern. */
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Resolve recorded mentions (userId + displayName) to `MessageMention`s with
 * `offset`/`length` by locating each `@displayName` token in the final body.
 * Repeated names map to successive occurrences; recorded mentions whose token
 * is no longer present are dropped. Output is sorted by offset.
 */
export function deriveMentions(body, recorded) {
    const out = [];
    const cursor = new Map();
    for (const r of recorded) {
        const token = '@' + r.displayName;
        const from = cursor.get(r.displayName) ?? 0;
        const re = new RegExp(escapeRegExp(token) + '(?![A-Za-z0-9])');
        const slice = body.slice(from);
        const rel = slice.search(re);
        if (rel === -1)
            continue;
        const offset = from + rel;
        out.push({ userId: r.userId, displayName: r.displayName, offset, length: token.length });
        cursor.set(r.displayName, offset + token.length);
    }
    return out.sort((a, b) => a.offset - b.offset);
}
//# sourceMappingURL=messaging.client.js.map